import { useSyncExternalStore } from 'react';

/**
 * Media the server has told us is not there.
 *
 * A file can go missing from storage while the record that points at it stays
 * behind. Rather than each list showing a broken tile, whoever tries to load
 * one says so here and every list drops it for the rest of the session.
 *
 * Only a 404 counts. Storage being briefly unreachable is not the same as a
 * file being gone, and hiding somebody's photographs because a bucket blinked
 * would be worse than showing nothing for a moment.
 */
const missing = new Set<string>();
const listeners = new Set<() => void>();

export function markMediaMissing(id: string): void {
  if (!id || missing.has(id)) return;
  missing.add(id);
  listeners.forEach((listen) => listen());
}

export function isMediaMissing(id?: string | null): boolean {
  return !!id && missing.has(id);
}

/** For tests */
export function clearMissingMedia(): void {
  missing.clear();
  listeners.forEach((listen) => listen());
}

const subscribe = (listen: () => void) => {
  listeners.add(listen);
  return () => {
    listeners.delete(listen);
  };
};

/**
 * Re-renders when something new turns out to be missing.
 *
 * Returns a predicate rather than the set itself: the set is mutated in place,
 * so handing it out would give callers something that never looks changed.
 */
export function useMissingMedia(): (id?: string | null) => boolean {
  const size = useSyncExternalStore(
    subscribe,
    () => missing.size,
    () => 0,
  );
  void size;
  return isMediaMissing;
}
